import React, { ReactNode, useMemo, useState } from "react";
import PaymentPanelNext from "../components/paymentPanel/PaymentPanelNext";
import { startPricingPlanChargingProcess } from "../api/startPricingPlanChargingProcess";
import useToast from "../hooks/useToast";
import { ToastSkin } from "./ToastContext";
import {isFreePricingPlanId} from "../consts/freePricingPlans";

type PricingPaymentConfig = {
    isOpen: boolean;
    pricingPlanId?: number;
    title: string;
    description?: ReactNode;
    amountUsd: number;
    onSuccess?: (successDetails?: any) => void;
};

type StartPricingPlanFlowInput = {
    selectedDomain?: string;
    platformId?: number;
    pricingPlanId: number;
    title: string;
    description?: ReactNode;
    amountUsd: number;
    onSuccess?: (successDetails?: any) => void;
    onDirectSuccess?: (response?: any) => void;
    onShopifyRedirect?: () => void;
};

type PaymentFlowContextProps = {
    startPricingPlanFlow: (input: StartPricingPlanFlowInput) => Promise<void>;
};

const defaultContextValue: PaymentFlowContextProps = {
    startPricingPlanFlow: async () => undefined,
};

export const PaymentFlowContext = React.createContext<PaymentFlowContextProps>(defaultContextValue);

export const PaymentFlowProvider = ({ children }: { children: ReactNode }) => {
    const { showToast } = useToast();
    const [pricingPayment, setPricingPayment] = useState<PricingPaymentConfig>({
        isOpen: false,
        title: "",
        amountUsd: 0,
    });

    const startPricingPlanFlow = async (input: StartPricingPlanFlowInput) => {
        const { selectedDomain, platformId, pricingPlanId, title, description, amountUsd, onSuccess, onDirectSuccess, onShopifyRedirect } = input;
        if (!selectedDomain) return;

        if (isFreePricingPlanId(pricingPlanId)) {
            const response = await startPricingPlanChargingProcess(selectedDomain, pricingPlanId);
            if (response) {
                onDirectSuccess?.(response?.data);
            } else {
                showToast({ skin: ToastSkin.error, text: "Failed to start pricing plan process. Please try again." });
            }
            return;
        }

        if (platformId === 1) {
            const response = await startPricingPlanChargingProcess(selectedDomain, pricingPlanId);
            if (response?.data?.url) {
                window.open(response.data.url);
                onShopifyRedirect?.();
            } else {
                showToast({ skin: ToastSkin.error, text: "Failed to load Shopify payment URL. Please try again." });
            }
            return;
        }

        setPricingPayment({
            isOpen: true,
            pricingPlanId,
            title,
            description,
            amountUsd,
            onSuccess,
        });
    };

    const value = useMemo(
        () => ({
            startPricingPlanFlow,
        }),
        []
    );

    return (
        <PaymentFlowContext.Provider value={value}>
            {children}
            <PaymentPanelNext
                isOpen={pricingPayment.isOpen}
                onClose={() => setPricingPayment((prev) => ({ ...prev, isOpen: false }))}
                title={pricingPayment.title}
                entityId={pricingPayment.pricingPlanId}
                amountUsd={pricingPayment.amountUsd}
                description={pricingPayment.description}
                paymentCall={startPricingPlanChargingProcess}
                afterSuccess={(successDetails) => {
                    pricingPayment.onSuccess?.(successDetails);
                    setPricingPayment((prev) => ({ ...prev, isOpen: false }));
                }}
            />
        </PaymentFlowContext.Provider>
    );
};
